// Web treatment for the app's content sheet.
//
// Hundreds of screens draw the same sheet: `backgroundColor: '#F3F6F8'` with
// the top two corners rounded, sliding over a teal hero and running off the
// bottom of a phone. In a browser the sheet often ends above the bottom of
// the column (content-sized, or a viewport taller than a phone), and a card
// with two square corners looks unfinished. This gives every such sheet
// matching bottom corners on web, applied inside the `react-native` shim's
// `StyleSheet.create`, so no screen changes and mobile never sees it.
//
// Deliberately narrow: only the sheet colour, only styles that round the top
// and say nothing about the bottom. White cards, modals and hero bands are
// untouched.

const SHEET_COLOR = '#F3F6F8';

type Style = Record<string, unknown>;

function isTopOnlySheet(style: Style): boolean {
  return (
    style.backgroundColor === SHEET_COLOR &&
    style.borderTopLeftRadius != null &&
    style.borderRadius == null &&
    style.borderBottomLeftRadius == null &&
    style.borderBottomRightRadius == null
  );
}

export function roundSheets<T extends Record<string, unknown>>(styles: T): T {
  let out: Record<string, unknown> | null = null;
  for (const key of Object.keys(styles)) {
    const style = styles[key];
    if (style && typeof style === 'object' && isTopOnlySheet(style as Style)) {
      const s = style as Style;
      if (!out) out = { ...styles };
      out[key] = {
        ...s,
        borderBottomLeftRadius: s.borderTopLeftRadius,
        borderBottomRightRadius: s.borderTopRightRadius ?? s.borderTopLeftRadius,
      };
    }
  }
  return (out ?? styles) as T;
}
